import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export const positionTeamsCache: {
	get: (key: string) => Promise<AggResponse<PositionTeamsResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<PositionTeamsResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<PositionTeamsResponse>>,
		expireWhen?: ((result: AggResponse<PositionTeamsResponse>) => boolean) | undefined,
	) => Promise<AggResponse<PositionTeamsResponse>>;
} = inMemoryCache<AggResponse<PositionTeamsResponse>>();

const QUERY_NAME = 'PositionTeams';
const positionTeamsQueryString = `query ${QUERY_NAME}($cloudId: ID!, $fieldId: ID!, $rql: String, $after: String, $first: Int) {
  radar_fieldValues(
    cloudId: $cloudId
    uniqueFieldId: $fieldId
    rql: $rql
    first: $first
    after: $after
  ) { 
    totalCount
    pageInfo {
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        __typename
        __isRadarFieldValue: __typename
        ... on RadarAriFieldValue {
          __typename
          isRestricted
          ari
          value {
            __typename
            ... on TeamV2 {
              __typename
              displayName
              id
              organizationId
              smallAvatarImageUrl
            }
          }
        }
      }
    }
  }
}`;

export type PositionTeam = {
	cursor: string;
	node: {
		isRestricted: boolean;
		ari: string;
		value: {
			__typename: string;
			displayName: string;
			id: string;
			organizationId: string;
			smallAvatarImageUrl: string;
		};
	};
};

export type PositionTeamsResponse = {
	radar_fieldValues: {
		edges?: PositionTeam[];
	};
};

export type PositionTeamsQueryVariables = {
	cloudId: string;
	first: number;
	fieldId: string;
	rql: string;
	after?: string;
};

export const positionTeamsQuery: (
	cloudId: string,
	first: number | undefined,
	rql: string,
	after?: string,
	headers?: Record<string, string>,
) => Promise<AggResponse<PositionTeamsResponse>> = (
	cloudId: string,
	first = 20,
	rql: string,
	after?: string,
	headers?: Record<string, string>,
) => {
	const fieldId = 'team';
	return positionTeamsCache.inMemoryDecorator(
		[cloudId, first, rql, fieldId, after].filter(Boolean).join('-'),
		async () => {
			if (!cloudId) {
				return {
					data: {
						radar_fieldValues: {
							edges: [],
						},
					},
				};
			}

			const response = await fetchAgg<PositionTeamsQueryVariables, PositionTeamsResponse>({
				graphQuery: positionTeamsQueryString,
				variables: { cloudId, first, rql, fieldId, after },
				operationName: QUERY_NAME,
				headers,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
